import React, { useState, useEffect } from 'react'
import { useLocaleStore } from '../../Contexts/LocaleContext'
import { useViewStore } from '../../Contexts/ViewContext'
import { useUtilsStore } from '../../Contexts/UtilsContext'
import { useDirectlineStore } from '../../Contexts/DirectlineContext'

import type { CustomMenu } from '../../../models/types'
import { AvailableViews, CurrentSubView, Routes } from '../../../models/enums'
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter'
import { mobileDisplays } from '../../../models/constants'

import DisplayIcon from './DisplayIcon'

interface DisplayGeneratorProps {
  indice: number
  view: number
  device: number
  handleClick?: () => void
  conditionalHandleClick?: () => void
  type: string
}

interface CustomMenuDisplayGeneratorProps {
  indice: number
  label: Record<string, string> | string
  device: number
  handleClick?: () => void
  conditionalHandleClick?: () => void
  icon: JSX.Element
  customDisplay?: string
  type: string
  name?: string
}

function instanceOfDisplayGeneratorProps(object: any): object is DisplayGeneratorProps {
  return 'view' in object && object.type === 'BaseMenu'
}

function instanceOfCustomMenuDisplayGeneratorProps(object: any): object is CustomMenuDisplayGeneratorProps {
  return 'icon' in object && object.type === 'CustomMenu'
}

const DisplayGenerator = (props: DisplayGeneratorProps | CustomMenuDisplayGeneratorProps): JSX.Element => {
  const translation = useLocaleStore((state) => state.currentTranslations)
  const locale = useLocaleStore((state) => state.locale)
  const isRTL = useLocaleStore((state) => state.isRTL)
  const isMobile = useViewStore((state) => state.isMobile)
  const themeColors = useViewStore((state) => state.themeColors)
  const setRoute = useViewStore((state) => state.actions.setRoute)
  const { setCurrentCustomView, setCurrentSubView } = useUtilsStore((state) => state.actions)
  const sendEvent = useDirectlineStore((state) => state.actions.sendEvent)

  const [label, setLabel] = useState<string>('')
  const [colorButtonHandle, setColorButtonHandle] = useState<string>('')

  /**
   * On component mount :
   *  - set label state depending on the locale
   */
  useEffect(() => {
    if (instanceOfDisplayGeneratorProps(props)) {
      setLabel(translation[props.view.toString()])
    } else if (instanceOfCustomMenuDisplayGeneratorProps(props)) {
      if (props.label && typeof props.label === 'string') {
        setLabel(props.label)
      } else if (props.label && typeof props.label === 'object') {
        setLabel(props.label[locale])
      }
    }
  }, [locale])

  useEffect(() => {
    let colorButtonHandle = ''
    // Handle hospitality menu icon color
    if (instanceOfCustomMenuDisplayGeneratorProps(props) && props.name) {
      if (props.name.toString().indexOf('whatToDo') > -1) {
        colorButtonHandle = themeColors.tobeDoneColor
      } else if (props.name.toString().indexOf('localProducts') > -1) {
        colorButtonHandle = themeColors.localProductsColor
      } else if (props.name.toString().indexOf('whereToEat') > -1) {
        colorButtonHandle = themeColors.whereToEatColor
      } else if (props.name.toString().indexOf('whereToSleep') > -1) {
        colorButtonHandle = themeColors.whereToSleepColor
      } else if (props.name.toString().indexOf('whatToSee') > -1) {
        colorButtonHandle = themeColors.tobeSeenColor
      } else if (props.name.toString().indexOf('agenda') > -1) {
        colorButtonHandle = themeColors.agendaColor
      } else if (props.name.toString().indexOf('services') > -1) {
        colorButtonHandle = themeColors.servicesColor
      }
    }
    setColorButtonHandle(colorButtonHandle)
  }, [label])

  /**
   * On call :
   *  - switch on chosen view
   *  - either display an overlaying page or redirect to another view
   *  - if handleClick is defined, call it
   */
  const handleClickOnCard = (): void => {
    if (instanceOfDisplayGeneratorProps(props)) {
      switch (props.view) {
        case AvailableViews.home:
          isMobile && props.conditionalHandleClick && props.conditionalHandleClick()
          sendEvent('showHome')
          break
        case AvailableViews.news:
          setRoute(Routes.News)
          break
        case AvailableViews.weather:
          setCurrentSubView(CurrentSubView.weather)
          break
        case AvailableViews.emergency:
          setCurrentSubView(CurrentSubView.emergency)
          break
      }
    } else if (instanceOfCustomMenuDisplayGeneratorProps(props)) {
      // Call props' closing function in mobile mode if asked by the custom component
      isMobile && props.conditionalHandleClick && props.conditionalHandleClick()
      // Display custom component if present
      if (props.customDisplay) {
        setCurrentCustomView({ ...props } as CustomMenu)
      }
    }

    props.handleClick && props.handleClick()
  }

  return mobileDisplays.includes(props.device) ? (
    /* On mobile / widget : display available views as lines */
    <button className={`rf-w-full rf-py-3 rf-flex rf-flex-row rf-items-center ${props.indice !== 0 && 'rf-border-t rf-border-t-menuBorder'}`} onClick={handleClickOnCard} aria-label={label}>
      {instanceOfDisplayGeneratorProps(props) ? (
        <DisplayIcon view={props.view} />
      ) : (
        <DisplayIcon icon={props.icon} className='rf-max-h-6 rf-max-w-6 rf-flex rf-items-center' color={colorButtonHandle} />
      )}
      <div className='rf-ml-4 rf-font-bold'>{capitalizeFirstLetter(label, locale)}</div>
    </button>
  ) : (
    /* On desktop / borne : display available views as buttons at the center of the screen */
    <button
      className='rf-h-36 rf-w-36 rf-p-4 rf-flex rf-flex-col rf-items-center rf-justify-between rf-rounded rf-shadow-[3px_3px_16px_#00000029]'
      style={{
        background: 'linear-gradient(to left top, #F2F3F6, #E5E6EC)'
      }}
      onClick={handleClickOnCard}
      aria-label={label}
    >
      {instanceOfDisplayGeneratorProps(props) ? <DisplayIcon view={props.view} className='rf-h-16' /> : <DisplayIcon icon={props.icon} className='rf-h-16 rf-w-16 rf-flex' color={colorButtonHandle} />}
      <div className={`rf-uppercase rf-text-[#575F6B] ${isRTL ? 'rf-text-arabic' : 'rf-text-xs'} large-vertical:rf-text-basecustomuppercase`}>{label}</div>
    </button>
  )
}

export default DisplayGenerator
